<script setup>
/**
 * One command that sends one real WhatsApp message, ready to paste.
 *
 * THE ADDRESS IS DERIVED FROM THE BROWSER, NOT WRITTEN DOWN ANYWHERE. `window.location
 * .origin` is the host this owner actually reaches us on — `waslo.byrukn.com` for one
 * workspace, something else for the next — and a constant here would be right for exactly
 * one environment and confidently wrong for every other. It also means the snippet becomes
 * CORRECT ON ITS OWN the day the public route is switched on for a workspace, with no
 * redeploy of this page: today the path exists on the sidecar and is not routed publicly,
 * which is what `ADDRESS_NOTE` says in one neutral sentence rather than pretending
 * otherwise.
 *
 * THE COMMAND IS ASSEMBLED IN CODE AND NOT IN `i18n.js`, and that is a hard constraint
 * rather than a preference: a JSON body is all braces, and a brace is vue-i18n's
 * interpolation syntax — `{"to": …}` in a message string is parsed as a placeholder named
 * `"to"` and renders as nothing at all. Wave 1 left the key out of the strings file on
 * purpose and said so beside `QUICKSTART`.
 *
 * WHICH PARTS ARE TRANSLATED AND WHICH ARE NOT: the sentences around the block are; the
 * block itself is a shell command and stays in the one language a shell speaks. The two
 * example values inside it (`+96550001234`, `order-1001`) are literals for the same
 * reason — they are sample data in a code listing, not prose, and the phone number is the
 * same one the placeholders elsewhere on this page already use.
 *
 * THE TEMPLATE NAME AND ITS BLANKS ARE THE REAL ONES. `params` is keyed by the template's
 * own variable names and the sidecar compares the two lists as sorted sets — a missing
 * name or an extra one is refused with `template_param_mismatch` — so a snippet with
 * invented keys would fail on the owner's first call. A template with no variables gets
 * no `params` field at all, which is exactly what the schema wants.
 */
import { computed, onScopeDispose, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useWasloI18n } from '../../i18n.js';

const props = defineProps({
  /**
   * A real approved message for the chosen number, or null when there is none yet. The
   * page picks it; this component only prints it.
   */
  template: { type: Object, default: null },
  /** Whether this workspace has a key at all — the one thing the snippet cannot supply. */
  hasKey: { type: Boolean, default: false },
});

const { t } = useWasloI18n();

/** The public API's own path (`BASE` + `MESSAGES_PATH`, `src/http/outbound.ts`). */
const SEND_PATH = '/outbound/v1/messages';

/** Sample data, not copy. See the block comment. */
const EXAMPLE_TO = '+96550001234';
const EXAMPLE_IDEMPOTENCY_KEY = 'order-1001';
const EXAMPLE_VALUE = 'PUT-VALUE-HERE';
/** Stands in until one of the workspace's own messages is approved. */
const EXAMPLE_TEMPLATE = { name: 'PUT-YOUR-MESSAGE-NAME-HERE', language: 'en' };

const copyState = ref('idle');
let resetTimer = null;

const clearTimer = () => {
  if (resetTimer !== null) {
    clearTimeout(resetTimer);
    resetTimer = null;
  }
};

onScopeDispose(clearTimer);

// Guarded rather than assumed: this component is mounted by a test runner as well as by a
// browser, and a bare `window` reference there is a thrown TypeError during render.
const origin = computed(() =>
  typeof window === 'undefined' ? '' : (window.location?.origin ?? '')
);

const address = computed(() => `${origin.value}${SEND_PATH}`);

const chosen = computed(() => props.template ?? EXAMPLE_TEMPLATE);

const command = computed(() => {
  const variables = props.template?.variables ?? [];
  const body = {
    idempotency_key: EXAMPLE_IDEMPOTENCY_KEY,
    to: EXAMPLE_TO,
    template: chosen.value.name,
    language: chosen.value.language,
  };
  if (variables.length > 0) {
    body.params = Object.fromEntries(
      variables.map(variable => [variable, EXAMPLE_VALUE])
    );
  }

  // Two-space JSON, indented one level inside the quoted body so the block reads as one
  // command rather than as a wall.
  const json = JSON.stringify(body, null, 2);
  return [
    `curl -X POST ${address.value} \\`,
    `  -H 'Authorization: Bearer ${t('WASLO.OUTBOUND.SETUP.QUICKSTART.KEY_PLACEHOLDER')}' \\`,
    `  -H 'Content-Type: application/json' \\`,
    `  -d '${json}'`,
  ].join('\n');
});

const copy = async () => {
  clearTimer();
  try {
    await copyTextToClipboard(command.value);
    copyState.value = 'copied';
  } catch {
    copyState.value = 'failed';
  }
  resetTimer = setTimeout(() => {
    copyState.value = 'idle';
    resetTimer = null;
  }, 4000);
};
</script>

<template>
  <div
    class="flex flex-col gap-4 p-5 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
    data-waslo-outbound-quickstart
  >
    <div class="flex flex-col gap-1">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('WASLO.OUTBOUND.SETUP.QUICKSTART.ADDRESS') }}
      </span>
      <!-- A URL is an identifier: `dir="ltr"` and `break-all`, never reflowed by an RTL page. -->
      <p
        class="min-w-0 mb-0 font-mono text-sm break-all text-n-slate-12"
        dir="ltr"
        data-waslo-outbound-quickstart-address
      >
        {{ address }}
      </p>
      <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
        {{ t('WASLO.OUTBOUND.SETUP.QUICKSTART.ADDRESS_NOTE') }}
      </p>
    </div>

    <!--
      README §2, in full: `pre-wrap` so the command wraps instead of stretching the page,
      `break-words` for the one long token (the address), a height cap and its own scroll
      container.

      A `div` with `whitespace-pre-wrap`, not a `<pre>` — `ConnectionTestPanel` already
      wrote down why: inside a real `<pre>` every character between the tags is content, so
      the formatter's own line breaks become a leading and a trailing blank line in what
      gets read and copied. Same rendering, and nothing here is whitespace-sensitive that
      the string itself does not already carry.
    -->
    <div
      class="p-4 overflow-auto font-mono text-xs leading-6 break-words whitespace-pre-wrap max-h-80 outline outline-1 -outline-offset-1 rounded-lg outline-n-weak bg-n-alpha-black2 text-n-slate-12"
      dir="ltr"
      data-waslo-outbound-quickstart-command
    >
      {{ command }}
    </div>

    <div class="flex flex-wrap items-center gap-3">
      <Button
        :label="
          copyState === 'copied'
            ? t('WASLO.OUTBOUND.COMMON.COPIED')
            : t('WASLO.OUTBOUND.SETUP.QUICKSTART.COPY')
        "
        :icon="copyState === 'copied' ? 'i-lucide-check' : 'i-lucide-copy'"
        variant="faded"
        color="slate"
        size="sm"
        data-waslo-outbound-quickstart-copy
        @click="copy"
      />
    </div>

    <p
      v-if="copyState === 'failed'"
      class="mb-0 text-sm text-n-ruby-9"
      data-waslo-outbound-quickstart-copy-failed
    >
      {{ t('WASLO.OUTBOUND.COMMON.COPY_FAILED') }}
    </p>

    <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
      {{ t('WASLO.OUTBOUND.SETUP.QUICKSTART.KEY_NOTE') }}
    </p>

    <!--
      The two things that are still missing, said where the missing thing would go. Both
      are ordinary states of a workspace being set up and both are instructions rather
      than warnings, so neither wears a status colour: a colour that fires when nothing is
      wrong is a colour nobody reads when something is (README §4).
    -->
    <p
      v-if="!hasKey"
      class="mb-0 text-sm leading-6 text-n-slate-11"
      data-waslo-outbound-quickstart-needs-key
    >
      {{ t('WASLO.OUTBOUND.SETUP.QUICKSTART.NEEDS_KEY') }}
    </p>
    <p
      v-if="template === null"
      class="mb-0 text-sm leading-6 text-n-slate-11"
      data-waslo-outbound-quickstart-needs-template
    >
      {{ t('WASLO.OUTBOUND.SETUP.QUICKSTART.NEEDS_TEMPLATE') }}
    </p>
  </div>
</template>
